import { useCallback, useState } from "react";
import { useMedia, useUploadMedia, useDeleteMedia } from "@/hooks/useMedia";
import { MediaItem } from "@/types/media";
import { PageHeader, Card } from "@/components/ui";
import { Icon } from "@/components/IconSprite";
import { formatDistanceToNow } from "date-fns";

const MediaLibrary = () => {
  const { data: media, isLoading } = useMedia();
  const uploadMutation = useUploadMedia();
  const deleteMutation = useDeleteMedia();

  const [isDragging, setIsDragging] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback(async (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);

    const files = Array.from(e.dataTransfer.files).filter((file) =>
      file.type.startsWith("image/")
    );

    for (const file of files) {
      try {
        await uploadMutation.mutateAsync(file);
      } catch (error) {
        console.error("Failed to upload file:", error);
      }
    }
  }, [uploadMutation]);

  const handleFileSelect = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);

    for (const file of files) {
      try {
        await uploadMutation.mutateAsync(file);
      } catch (error) {
        console.error("Failed to upload file:", error);
      }
    }

    e.target.value = "";
  }, [uploadMutation]);

  const handleDelete = async (mediaId: string) => {
    try {
      await deleteMutation.mutateAsync(mediaId);
      setDeleteConfirmId(null);
    } catch (error) {
      console.error("Failed to delete media:", error);
    }
  };

  const handleCopyUrl = (item: MediaItem) => {
    navigator.clipboard.writeText(item.url).then(() => {
      setCopiedId(item.mediaId);
      setTimeout(() => setCopiedId(null), 2000);
    });
  };

  const formatFileSize = (bytes: number | null): string => {
    if (!bytes) return "Unknown size";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  if (isLoading) {
    return (
      <div className="page">
        <PageHeader
          label="Content"
          title="Media Library"
          description="Upload and manage images for your content."
        />
        <Card>
          <p className="meta" style={{ textAlign: "center", padding: "32px 0" }}>Loading...</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="page">
      <PageHeader
        label="Content"
        title="Media Library"
        description="Upload and manage images for your content."
      />

      {/* Upload Zone */}
      <Card>
        <div
          className="dz"
          style={isDragging ? { borderColor: "var(--g)", background: "var(--g-tint)" } : undefined}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <div className="dz-i"><Icon name="img" /></div>
          <b>{uploadMutation.isPending ? "Uploading..." : "Drop images here"}</b>
          <span>or click to browse</span>
          <div style={{ marginTop: 12 }}>
            <label className="btn out sm" style={{ cursor: "pointer" }}>
              <Icon name="upload" />
              Browse Files
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={handleFileSelect}
                style={{ display: "none" }}
                disabled={uploadMutation.isPending}
              />
            </label>
          </div>
        </div>
      </Card>

      {/* Media Grid */}
      {media && media.length > 0 ? (
        <div className="card">
          <div className="card-h">
            <div>
              <h2>Media <span className="b def">{media.length} files</span></h2>
            </div>
          </div>
          <div className="card-b">
            <div className="mgrid">
              {media.map((item) => {
                const isCopied = copiedId === item.mediaId;
                const isConfirming = deleteConfirmId === item.mediaId;
                return (
                  <div
                    key={item.mediaId}
                    className="mitem"
                    style={
                      isCopied
                        ? { borderColor: "var(--g)" }
                        : isConfirming
                        ? { borderColor: "var(--err-bd)" }
                        : undefined
                    }
                  >
                    <div className="mthumb">
                      <img
                        src={item.url}
                        alt={item.originalFilename || item.filename}
                        style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }}
                      />
                      <div
                        className="mov"
                        style={
                          isConfirming
                            ? { opacity: 1, flexDirection: "column", gap: 7 }
                            : isCopied
                            ? { opacity: 1 }
                            : undefined
                        }
                      >
                        {isConfirming ? (
                          <>
                            <span style={{ fontSize: 11, color: "#fff", fontWeight: 640 }}>Delete?</span>
                            <span style={{ display: "flex", gap: 6 }}>
                              <button
                                type="button"
                                className="btn dan sm"
                                onClick={() => handleDelete(item.mediaId)}
                                disabled={deleteMutation.isPending}
                              >
                                Confirm
                              </button>
                              <button
                                type="button"
                                className="btn sm"
                                style={{ background: "rgba(255,255,255,.16)", color: "#fff", borderColor: "rgba(255,255,255,.2)" }}
                                onClick={() => setDeleteConfirmId(null)}
                              >
                                Cancel
                              </button>
                            </span>
                          </>
                        ) : (
                          <>
                            <button
                              type="button"
                              className="b2"
                              style={isCopied ? { background: "var(--g)", borderColor: "var(--g)" } : undefined}
                              onClick={() => handleCopyUrl(item)}
                              title="Copy URL"
                            >
                              <Icon name={isCopied ? "check" : "copy"} />
                            </button>
                            <button
                              type="button"
                              className="b2 d"
                              onClick={() => setDeleteConfirmId(item.mediaId)}
                              title="Delete"
                            >
                              <Icon name="trash" />
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                    <div className="mcap">
                      <b title={item.originalFilename || item.filename}>
                        {item.originalFilename || item.filename}
                      </b>
                      <span>
                        {formatFileSize(item.sizeBytes)}
                        {item.createdAt && (
                          <> · {formatDistanceToNow(new Date(item.createdAt), { addSuffix: true })}</>
                        )}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      ) : (
        <Card>
          <div className="empty">
            <div className="empty-i"><Icon name="img" /></div>
            <b>No media uploaded yet</b>
          </div>
        </Card>
      )}
    </div>
  );
};

export default MediaLibrary;
